import { Loader } from "@googlemaps/js-api-loader"
import { Controller } from "@hotwired/stimulus"
import { GoogleMapsLoader } from "./map/google_maps_loader"
import { PinMapHandler } from "./map/pin_map_handler"
import { FenceDrawingHandler } from "./map/fence_drawing_handler"
import { MapSearchHandler } from "./map/map_search_handler"

export default class extends Controller {
  static values = {
    apiKey: String,
    typeMap: { type: String, default: 'pin' },
    mode: { type: String, default: 'none' },
    lat: Number,
    lng: Number,
    fenceType: { type: String, default: 'polygon' }
  }

  static targets = ['map', 'lat', 'lng', 'search', 'coords', 'shape', 'pull', 'color_picker', 'button', 'modalTitle', 'footerInstruction', 'instruction', 'openMapBtn', 'mapTypeDropdown', 'mapTypeMenu', 'mapTypeText']

  initialize() {
    this.setupInitialState()
    this.initializeLoaders()
    this.initializeHandlers()
    this.loadGoogleMaps()
    this.setupEventListeners()
  }

  connect() {
    if (!this._initialized) {
      this.initialize()
      this._initialized = true
    }
  }

  setupInitialState() {
    this.editMode = null
    this.markers = []
    this.path = []
    this.searchBox = null
    this.mapInitialized = false
    this.isEditMode = this.modeValue === 'edit'
    this.currentMapType = 'roadmap'

    const fenceTypeSelect = document.getElementById('fence_type')
    if (fenceTypeSelect?.value && fenceTypeSelect.value !== 'prompt') {
      this.fenceTypeValue = fenceTypeSelect.value
    }

    window.mapController = this
  }

  initializeLoaders() {
    this._loader = new Loader({
      apiKey: this.apiKeyValue,
      libraries: ["maps", "places", "geometry", "drawing"],
      version: "weekly"
    })

    this._gMaps = this._loader.importLibrary('maps')
    this._gMarker = this._loader.importLibrary('marker')
  }

  initializeHandlers() {
    this.loader = new GoogleMapsLoader(this.apiKeyValue)
    this.pinHandler = new PinMapHandler(this)
    this.searchHandler = new MapSearchHandler(this)
  }

  setupEventListeners() {
    const fenceTypeSelect = document.getElementById('fence_type')
    if (fenceTypeSelect) {
      fenceTypeSelect.addEventListener('change', (e) => {
        this.setFenceType(e.target.value)
      })
    }
  }

  async loadGoogleMaps() {
    try {
      await this.loader.load()

      switch (this.typeMapValue) {
        case 'pin':
          this.initPinMap()
          break
        case 'fence':
          this.initFenceMap()
          break
      }
    } catch (error) {
      this.showMapError(`Erro: ${error.message}. Verifique a API Key.`)
    }
  }

  initPinMap() {
    this._gMaps.then(({ Map }) => {
      if (this.hasMapTarget) {
        if (this.latValue && this.lngValue && this.modeValue == 'none') {
          this.map = new Map(this.mapTarget, { zoom: 10, center: { lat: this.latValue, lng: this.lngValue } })

          this._gMarker.then(({ Marker }) => {
            this._marker = new Marker({ position: { lat: this.latValue, lng: this.lngValue }, map: this.map, editable: false, visible: true })
          })
        } else {
          if (this.modeValue == 'edit') {
            this.map = new Map(this.mapTarget, { zoom: 6, center: { lat: this.latValue, lng: this.lngValue } })

            this._gMarker.then(({ Marker }) => {
              this._marker = new Marker({ position: { lat: this.latValue, lng: this.lngValue }, map: this.map, editable: true, visible: true })
            })
            this.map.addListener('click', this.onMapClick.bind(this))
          } else {
            this.map = new Map(this.mapTarget, { zoom: 4, center: { lat: -14, lng: -55 } })
            this.map.addListener('click', this.onMapClick.bind(this))
          }
        }
      }
    })
  }

  async initFenceMap() {
    this.editMode = /edit|new/.test(document.location.href) || this.modeValue === 'edit'

    if (!this.hasMapTarget) return

    this.map = new google.maps.Map(this.mapTarget, {
      zoom: 4,
      center: { lat: -14, lng: -55 },
      mapTypeControl: false, // Desabilita o controle nativo
      streetViewControl: false,
      fullscreenControl: false, // Desabilitado - usaremos nosso próprio
      zoomControl: false, // Desabilitado - usaremos nosso próprio
      rotateControl: false, // Desabilitado para manter limpo
      scaleControl: false, // Desabilitado para manter limpo
      mapTypeId: google.maps.MapTypeId[this.currentMapType.toUpperCase()]
    })

    this.fenceHandler = new FenceDrawingHandler(this, this.map)
    await this.fenceHandler.init()

    if (this.hasSearchTarget) {
      this.searchHandler.init()
    }

    this.updateInstructions()
    this.mapInitialized = true

    // Inicializar botões de controle
    this.initializeControlButtons()

    // Carregar coordenadas existentes automaticamente no modo show
    if (this.modeValue === 'show') {
      setTimeout(() => {
        this.loadExistingCoords()
      }, 1000)
    }
  }

  onMapClick(event) {
    if (this.typeMapValue === 'pin') {
      this.pinHandler?.handleClick(event)
    }
  }

  setFenceType(type) {
    if (!type || type === 'prompt' || type === '') {
      console.log('❌ Tipo inválido, não fazendo nada')
      return
    }

    // console.log('🧹 Limpando área ao mudar tipo de cerca')
    // this.clearAllAreaData()

    this.fenceTypeValue = type
    console.log('✅ Novo tipo definido:', this.fenceTypeValue)

    if (this.fenceHandler) {
      this.fenceHandler.clearAll()
      this.updateInstructions()
    }

    this.notifyFenceControllerAreaCleared()
  }

  startDrawing() {
    if (this.fenceHandler && this.fenceTypeValue && this.fenceTypeValue !== 'prompt') {
      this.fenceHandler.setDrawingMode(this.fenceTypeValue)
      this.updateInstructions()
    }
  }

  stopDrawing() {
    if (this.fenceHandler) {
      this.fenceHandler.stopDrawing()
    }
  }

  reactivateDrawing() {
    setTimeout(() => {
      if (this.fenceTypeValue && this.fenceTypeValue !== 'prompt' && this.fenceTypeValue !== '') {
        this.startDrawing()
      }
    }, 200)
  }

  updateInstructions() {
    if (this.hasModalTitleTarget) {
      this.modalTitleTarget.textContent = this.fenceTypeValue === 'circle'
        ? 'Desenhar Círculo da Cerca'
        : 'Desenhar Área da Cerca'
    }

    if (this.hasFooterInstructionTarget) {
      this.footerInstructionTarget.textContent = this.fenceTypeValue === 'circle'
        ? 'Clique no mapa para posicionar o círculo'
        : 'Clique no mapa para desenhar a cerca'
    }
  }

  initializeControlButtons() {
    // Inicializar dropdown de tipo de mapa
    this.updateMapTypeText(this.currentMapType)
  }


  changeColor() {
    if (this.fenceHandler) {
      const color = this.getCurrentColor()
      this.fenceHandler.updateColor(color)
    }
  }

  clearAll() {
    if (this.fenceHandler) {
      this.fenceHandler.clearAll()
    }
    this.path = []
    this.markers = []
  }

  loadExistingCoords() {
    if (this.fenceHandler) {
      this.fenceHandler.loadExistingCoords()
    }
  }

  getCurrentColor() {
    const colorInput = document.querySelector('input[name="electronic_fence[color]"]')
    return colorInput ? colorInput.value : '#3260a8'
  }

  notifyFenceControllerAreaCleared() {
    const fenceElement = document.querySelector('[data-controller*="electronic-fence"]')
    if (fenceElement?.electronicFence) {
      fenceElement.electronicFence.areaSelected = false
      if (fenceElement.electronicFence.hideAreaStatus) {
        fenceElement.electronicFence.hideAreaStatus()
      }
      if (fenceElement.electronicFence.clearAreaError) {
        fenceElement.electronicFence.clearAreaError()
      }
    }
  }

  showMapError(message) {
    if (this.hasMapTarget) {
      this.mapTarget.innerHTML = `
        <div class="flex items-center justify-center h-full bg-red-50 border-2 border-red-200 rounded-lg">
          <div class="text-center p-8">
            <div class="text-6xl mb-4">❌</div>
            <h3 class="text-xl font-bold text-red-700 mb-2">Erro ao carregar Google Maps</h3>
            <p class="text-red-600">${message}</p>
            <button onclick="location.reload()" class="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
              Tentar Novamente
            </button>
          </div>
        </div>
      `
    }
  }

  // Métodos para controle de tipo de mapa (dropdown)
  toggleMapTypeDropdown(event) {
    event.stopPropagation()
    if (this.hasMapTypeMenuTarget) {
      this.mapTypeMenuTarget.classList.toggle('hidden')
    }
    
    
    // Fechar dropdown ao clicar fora
    document.addEventListener('click', this.closeDropdown.bind(this), { once: true })
  }

  selectMapType(event) {
    const mapType = event.target.dataset.mapType
    this.currentMapType = mapType
    
    if (this.map) {
      this.map.setMapTypeId(google.maps.MapTypeId[mapType.toUpperCase()])
    }
    
    this.updateMapTypeText(mapType)
    this.mapTypeMenuTarget.classList.add('hidden')
  }

  updateMapTypeText(mapType) {
    const typeNames = {
      'roadmap': 'Ruas',
      'satellite': 'Satélite', 
      'hybrid': 'Híbrido'
    }
    
    if (this.hasMapTypeTextTarget) {
      this.mapTypeTextTarget.textContent = typeNames[mapType] || 'Ruas'
    }
  }

  closeDropdown() {
    if (this.hasMapTypeMenuTarget) {
      this.mapTypeMenuTarget.classList.add('hidden')
    }
  }


}